import { DragDropModule } from '@angular/cdk/drag-drop';
import { HttpClientModule } from '@angular/common/http';
import { ApplicationRef, DoBootstrap, ErrorHandler, inject, NgModule, NO_ERRORS_SCHEMA, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApiModule, Configuration } from '@estel-numerique/galaclick-api';
import { environment } from '../environments/environment';
//services
import { UtilService } from '@services/index';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import { CtrlAccess } from '../services/util/ctrlAccess';
import { AccueilComponent } from './accueil/accueil.component';
import { AppRoutingModule } from './app-routing.module';
//components
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorDefaultOptions, MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouteReuseStrategy } from '@angular/router';
import { CustomPaginatorIntl } from '@classes/custom-paginator';
import { RouteStrategy } from '@classes/route-strategy';
import { provideTranslateService, TranslateService, MissingTranslationHandler } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from "@sentry/angular";
import { appInitializerFactory } from './app-initializer';
import { AppComponent } from './app.component';
import { AProposComponent } from './apropos/apropos.component';
import { AuthComponent } from './auth/auth.component';
import { MdpOublieComponent } from './auth/mdp-oublie/mdp-oublie.component';
import { ContactAjoutComponent } from './contacts/pages/contact-ajout/contact-ajout.component';
import { IncarnationComponent } from './incarnation/incarnation.component';
import { PageInaccessibleComponent } from './divers/pageinaccessible/pageinaccessible.component';
import { PageNotFoundComponent } from './divers/pagenotfound/pagenotfound.component';
import { FicheClientModule } from './ficheclient/ficheclient.module';
import { IconEchange, ListeSyncDetailsComponent, ListeSyncListeComponent, SyncDetailsComponent } from './listessynchro/listesync.component';
import { MassExportComponent } from './massExport/massExport.component';
import { MenuGalaComponent } from './menu/menu-gala.component';
import { MonCompteComponent } from './moncompte/moncompte.component';
import { PilotageComponent } from './pilotage/pilotage.component';
import { ProfilCreerComponent, ProfilDetailsComponent, ProfilListeComponent } from './profil/profil.component';
import { RechClientComponent } from './rech-client/rech-client.component';
import { SharedModule } from './shared.module';
import { PlanJourAgentComponent } from './suivi/plans-tournee/components/plan-jour-agent/plan-jour-agent.component';
import { PlansTourneeComponent } from './suivi/plans-tournee/pages/plans-tournee.component';
import { PFClientListeComponent } from './suiviagents/pfClient.component';
import { PFClientMoveMultiComponent } from './suiviagents/pfClientMoveMulti.component';
import { ClientsComponent } from './suiviclients/clients.component';
import { HistoAgentComponent } from './suiviclients/histo-agent/histo-agent.component';
import { TraductionAbsenteHandler } from '../services/traduction-absente.handler';

registerLocaleData(localeFr);

@NgModule({
    imports: [
        AppComponent,
        SharedModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        DragDropModule,
        FicheClientModule,
        MatDialogModule,
        MatButtonModule,
        MatCardModule,
        MatProgressSpinnerModule,
        CdkAccordionModule,
        MatMenuModule,
        MatSnackBarModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatMomentDateModule,
        MatInputModule,
        MatTooltipModule,
        MatMenuModule,
        ApiModule.forRoot(confFactory),
        MdpOublieComponent,
        AProposComponent,
        AccueilComponent,
        AuthComponent,
        IncarnationComponent,
        ProfilListeComponent,
        ProfilCreerComponent,
        ProfilDetailsComponent,
        PageNotFoundComponent,
        ListeSyncListeComponent,
        ListeSyncDetailsComponent,
        MonCompteComponent,
        ClientsComponent,
        HistoAgentComponent,
        PFClientListeComponent,
        PFClientMoveMultiComponent,
        SyncDetailsComponent,
        IconEchange,
        PlansTourneeComponent,
        PlanJourAgentComponent,
        PilotageComponent,
        MassExportComponent,
        MenuGalaComponent,
        RechClientComponent,
        ContactAjoutComponent,
        PageInaccessibleComponent
    ],
    exports: [],
    providers: [
        UtilService,
        CtrlAccess,
        {
            provide: RouteReuseStrategy,
            useClass: RouteStrategy
        }, provideTranslateService({
            fallbackLang: 'fr',
            missingTranslationHandler: { provide: MissingTranslationHandler, useClass: TraductionAbsenteHandler },
            loader: provideTranslateHttpLoader({ prefix: "./i18n/", suffix: ".json" }),
        }),
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntl
        },
        {
            provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
            useValue: {
                pageSizeOptions: [10, 15, 25, 50, 100],
                pageSize: 15,
                showFirstLastButtons: true
            } as MatPaginatorDefaultOptions
        },
        {
            // useFactory (et non useValue) : evalue environment.isDebugMode/logSentry a la creation
            // de l'injector (apres injection de la config runtime dans main.ts), pas au chargement du module
            provide: ErrorHandler,
            useFactory: () => (!environment.isDebugMode)
                ? Sentry.createErrorHandler({
                    showDialog: false,
                    logErrors: environment.logSentry
                })
                : new ErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: OVERLAY_DEFAULT_CONFIG,
            useValue: {
                usePopover: false
            }
        },
        provideAppInitializer(() => inject(KeycloakAuthService).init()),
        provideAppInitializer(() => {
            const initializerFn = (appInitializerFactory)(inject(Sentry.TraceService), inject(TranslateService));
            return initializerFn();
        })
    ],
    schemas: [NO_ERRORS_SCHEMA]
})
export class AppModule implements DoBootstrap {
    private readonly appRef = inject(ApplicationRef);

    /** Démarre le composant racine (autonome) */
    ngDoBootstrap() {
        this.appRef.bootstrap(AppComponent);
    }
}
export function confFactory(): Configuration { return new Configuration({ basePath: environment.wsURL, apiKeys: { Authorization: sessionStorage.getItem("tokenreswel") } }); };
